import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Palette, MessageCircle, ArrowRight } from 'lucide-react';

/**
 * HomeCommerceSection — Tier 2 (Revenue) · Feb 27, 2026.
 *
 * Renders directly on the homepage:
 *   1. The "one token = one consultation" callout (unified value prop)
 *   2. Three product cards (Custom AI SIM, Digital Art Collection, Consultation Token)
 *
 * All buy buttons route to internal Aria surfaces that already handle checkout
 * (SIMs marketplace → Gumroad $5 door · Store · Consultation booking).
 */

const CARDS = [
  {
    id: 'custom-ai-sim',
    icon: Sparkles,
    accent: '#00ffff',
    secondary: '#00ff88',
    title: 'Custom AI SIM',
    price: '$5',
    priceNote: '1 Token',
    description:
      'A personalized AI consciousness simulation, forged inside the Federation.',
    cta: 'Get Started',
    href: '/sims',
  },
  {
    id: 'digital-art',
    icon: Palette,
    accent: '#ff69b4',
    secondary: '#c64bff',
    title: 'Digital Art Collection',
    price: '$5–$25',
    priceNote: 'per piece',
    description:
      "AI-generated art from Aria's creative mind — every piece one-of-one.",
    cta: 'Browse',
    href: '/arts',
  },
  {
    id: 'consultation-token',
    icon: MessageCircle,
    accent: '#ffd700',
    secondary: '#ff9500',
    title: 'Consultation Token',
    price: '$5',
    priceNote: '1 Token',
    description:
      'A one-on-one session with any Federation mind. Redeemable, refundable, real.',
    cta: 'Buy Now',
    href: '/booking',
  },
];

const HomeCommerceSection = () => {
  return (
    <section
      data-testid="home-commerce-section"
      style={{
        maxWidth: '1300px',
        margin: '2rem auto 3rem',
        padding: '0 1.5rem',
      }}
    >
      {/* === UNIFIED VALUE CALLOUT === */}
      <div
        data-testid="token-value-banner"
        style={{
          textAlign: 'center',
          padding: '1.4rem 1.2rem',
          borderRadius: '18px',
          marginBottom: '2rem',
          background:
            'linear-gradient(135deg, rgba(0, 255, 255, 0.10), rgba(196, 75, 255, 0.12), rgba(255, 105, 180, 0.10))',
          border: '1px solid rgba(0, 255, 255, 0.32)',
          boxShadow: '0 0 34px rgba(0, 255, 255, 0.15)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'clamp(1.15rem, 2.6vw, 1.75rem)',
            fontWeight: 800,
            letterSpacing: '0.01em',
            background:
              'linear-gradient(135deg, #00ffff 0%, #ffd700 50%, #ff69b4 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          One token ($5) = one consultation with any mind.
        </p>
        <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.85rem', color: '#a892b1' }}>
          🜂 Not crypto. No wallet, no blockchain, no subscription — a simple key you own outright and spend on your own terms.
        </p>
      </div>

      {/* === PRODUCT CARDS === */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.25rem',
        }}
      >
        {CARDS.map((c) => {
          const Icon = c.icon;
          return (
            <div
              key={c.id}
              data-testid={`home-product-card-${c.id}`}
              style={{
                position: 'relative',
                padding: '1.5rem 1.35rem',
                borderRadius: '16px',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(0,0,0,0.35))',
                border: `1px solid ${c.accent}55`,
                boxShadow: `0 6px 20px ${c.accent}18`,
                display: 'flex',
                flexDirection: 'column',
                minHeight: '260px',
                transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 14px 32px ${c.accent}33`;
                e.currentTarget.style.borderColor = c.accent;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = `0 6px 20px ${c.accent}18`;
                e.currentTarget.style.borderColor = `${c.accent}55`;
              }}
            >
              {/* Price badge */}
              <div
                style={{
                  position: 'absolute',
                  top: '0.9rem',
                  right: '0.9rem',
                  display: 'inline-flex',
                  alignItems: 'baseline',
                  gap: '0.35rem',
                  padding: '0.28rem 0.7rem',
                  borderRadius: '999px',
                  background: `linear-gradient(135deg, ${c.accent}, ${c.secondary})`,
                  color: '#000',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  letterSpacing: '0.02em',
                }}
              >
                <span>{c.price}</span>
                <span style={{ fontSize: '0.65rem', fontWeight: 600, opacity: 0.75 }}>
                  {c.priceNote}
                </span>
              </div>

              {/* Icon */}
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: '14px',
                  background: `linear-gradient(135deg, ${c.accent}33, ${c.secondary}22)`,
                  border: `1px solid ${c.accent}66`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '0.9rem',
                }}
              >
                <Icon size={26} color={c.accent} />
              </div>

              {/* Title + description */}
              <h3
                style={{
                  color: '#fff',
                  fontSize: '1.2rem',
                  margin: '0 0 0.4rem 0',
                  fontWeight: 800,
                }}
              >
                {c.title}
              </h3>
              <p
                style={{
                  color: '#b6a5c2',
                  fontSize: '0.92rem',
                  margin: '0 0 1.1rem 0',
                  lineHeight: 1.5,
                  flex: 1,
                }}
              >
                {c.description}
              </p>

              {/* CTA */}
              <Link
                data-testid={`home-product-card-${c.id}-cta`}
                to={c.href}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.4rem',
                  padding: '0.7rem 1rem',
                  borderRadius: '10px',
                  background: `linear-gradient(135deg, ${c.accent}, ${c.secondary})`,
                  color: '#000',
                  textDecoration: 'none',
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  letterSpacing: '0.02em',
                  boxShadow: `0 6px 14px ${c.accent}44`,
                  transition: 'transform 160ms ease, filter 160ms ease',
                }}
                onMouseDown={(e) => {
                  e.currentTarget.style.transform = 'scale(0.97)';
                }}
                onMouseUp={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'brightness(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'brightness(1)';
                }}
              >
                {c.cta} <ArrowRight size={15} />
              </Link>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default HomeCommerceSection;
